<template>
  <tr v-if="!compact" class="row" :class="{ 'is-inactive': !asset.isActive }">
    <td class="cell-thumb">
      <div class="asset-thumb">
        <img v-if="imageSrc" :src="imageSrc" alt="Asset thumbnail" @error="useDefaultImage" />
        <img v-else-if="settled" :src="noImageAvailable" alt="No image available" />
      </div>
    </td>
    <td class="cell-name">
      <div class="name-cell">
        <span class="name-text">{{ asset.name }}</span>
        <span v-if="!asset.isActive" class="status-badge status-inactive">Inactive</span>
      </div>
    </td>
    <td class="cell-desc">{{ asset.description || '—' }}</td>
    <td class="cell-actions">
      <DisplayOverlayWindowButton ref="detailsBtn" buttonText="Details" windowTitle="Asset Details">
        <div class="asset-details">
          <div class="details-image">
            <img v-if="imageSrc" :src="imageSrc" alt="Asset image" @error="useDefaultImage" />
            <img v-else-if="settled" :src="noImageAvailable" alt="No image available" />
          </div>
          <div class="details-body">
            <div class="details-eyebrow">Asset</div>
            <h2 class="details-title">{{ asset.name }}</h2>
            <p class="details-desc">{{ asset.description || 'No description available.' }}</p>
            <div class="details-meta">
              <span class="meta-label">Status</span>
              <span class="status-badge" :class="asset.isActive ? 'status-active' : 'status-inactive'">
                {{ asset.isActive ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="details-actions">
              <button v-if="asset.isActive" class="btn-danger" :disabled="isUpdating" @click="deleteAsset">
                {{ isUpdating ? 'Working…' : 'Delete' }}
              </button>
              <button v-else class="btn-primary" :disabled="isUpdating" @click="reactivateAsset">
                {{ isUpdating ? 'Working…' : 'Reactivate' }}
              </button>
            </div>
          </div>
        </div>
      </DisplayOverlayWindowButton>
    </td>
  </tr>

  <article v-else class="card" :class="{ 'is-inactive': !asset.isActive }">
    <div class="card-header">
      <div class="asset-thumb">
        <img v-if="imageSrc" :src="imageSrc" alt="Asset thumbnail" @error="useDefaultImage" />
        <img v-else-if="settled" :src="noImageAvailable" alt="No image available" />
      </div>
      <div class="card-meta">
        <div class="card-name">{{ asset.name }}</div>
        <span v-if="!asset.isActive" class="status-badge status-inactive">Inactive</span>
      </div>
    </div>
    <p v-if="asset.description" class="card-desc">{{ asset.description }}</p>
    <div class="card-actions">
      <DisplayOverlayWindowButton ref="detailsBtn" buttonText="Details" windowTitle="Asset Details">
        <div class="asset-details">
          <div class="details-image">
            <img v-if="imageSrc" :src="imageSrc" alt="Asset image" @error="useDefaultImage" />
            <img v-else-if="settled" :src="noImageAvailable" alt="No image available" />
          </div>
          <div class="details-body">
            <div class="details-eyebrow">Asset</div>
            <h2 class="details-title">{{ asset.name }}</h2>
            <p class="details-desc">{{ asset.description || 'No description available.' }}</p>
            <div class="details-meta">
              <span class="meta-label">Status</span>
              <span class="status-badge" :class="asset.isActive ? 'status-active' : 'status-inactive'">
                {{ asset.isActive ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div class="details-actions">
              <button v-if="asset.isActive" class="btn-danger" :disabled="isUpdating" @click="deleteAsset">
                {{ isUpdating ? 'Working…' : 'Delete' }}
              </button>
              <button v-else class="btn-primary" :disabled="isUpdating" @click="reactivateAsset">
                {{ isUpdating ? 'Working…' : 'Reactivate' }}
              </button>
            </div>
          </div>
        </div>
      </DisplayOverlayWindowButton>
    </div>
  </article>
</template>

<script setup>
import { defineEmits, defineOptions, defineProps, ref, watch } from 'vue';
import DisplayOverlayWindowButton from '@/components/buttons/DisplayOverlayWindowButton.vue';
import AssetService from '@/services/AssetService';
import { useDefaultImage } from '@/utils/ImageUtils';
import organizationSasService from '@/services/OrganizationSasService';
import noImageAvailable from '@/assets/no-image-available.svg';

defineOptions({ name: "AssetTableItem" })

const props = defineProps({
  asset: { type: Object },
  compact: { type: Boolean, default: false }
})

const emits = defineEmits(["onAssetModified"])

const isUpdating = ref(false);
const imageSrc = ref(null);
const settled = ref(false);   // true once signing resolves — gates the no-image fallback so it doesn't flash while loading

const deleteAsset = async () => { await updateAsset(false); };
const reactivateAsset = async () => { await updateAsset(true); };

const updateAsset = async (isActive) => {
  isUpdating.value = true;
  try {
    await AssetService.deleteAssetAsync(props.asset.organizationId, props.asset.id, isActive);
  } finally {
    isUpdating.value = false;
    emits("onAssetModified")
  }
}

watch(
  () => props.asset,
  async (newVal) => {
    if (!newVal) return;
    settled.value = false;
    imageSrc.value = null;
    try {
      imageSrc.value = await organizationSasService.getSignedUrl(newVal.imagePath);
    } finally {
      settled.value = true;
    }
  },
  { immediate: true }
);
</script>

<style scoped>
/* ----- Desktop row ----- */
.row {
  transition: background 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.row:last-child { border-bottom: none; }
.row:hover { background: rgba(100, 255, 218, 0.04); }

.row.is-inactive .cell-thumb,
.row.is-inactive .cell-name,
.row.is-inactive .cell-desc {
  opacity: 0.5;
}

.row td {
  padding: 12px 16px;
  vertical-align: middle;
  font-size: 0.92rem;
}

.cell-thumb { width: 90px; }
.cell-name { color: #ffffff; font-weight: 600; }
.cell-desc { color: #b0bec5; }
.cell-actions { width: 120px; text-align: right; white-space: nowrap; }

.name-cell { display: flex; align-items: center; gap: 8px; }

.asset-thumb {
  width: 64px;
  height: 48px;
  border-radius: 8px;
  overflow: hidden;
  background: #1c272c;
  display: flex;
  align-items: center;
  justify-content: center;
}

.asset-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ----- Mobile card ----- */
.card {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.card.is-inactive .card-header,
.card.is-inactive .card-desc {
  opacity: 0.6;
}

.card-header { display: flex; gap: 12px; align-items: center; }

.card-header .asset-thumb {
  width: 56px;
  height: 56px;
  flex: 0 0 56px;
}

.card-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
  word-break: break-word;
}

.card-desc {
  font-size: 0.88rem;
  color: #b0bec5;
  margin: 0;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-actions { display: flex; justify-content: flex-end; }

/* ----- Status badges ----- */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.status-active {
  background: rgba(74, 222, 128, 0.12);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
}

.status-inactive {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
}

/* ----- Details dialog ----- */
.asset-details {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 8px;
  color: #fff;
  width: min(620px, 78vw);
}

.details-image {
  flex: 0 0 200px;
  width: 200px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  background: #1c272c;
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}

.details-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.details-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.details-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.details-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.details-desc {
  color: #b0bec5;
  margin: 0;
  line-height: 1.55;
}

.details-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}

.meta-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8aa0aa;
  font-weight: 700;
}

.details-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.btn-primary {
  padding: 10px 18px;
  border-radius: 10px;
  background: var(--duallo-accent-gradient);
  color: #071514;
  font-weight: 700;
  border: none;
  cursor: pointer;
  font: inherit;
}

.btn-danger {
  padding: 10px 18px;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
  font-weight: 700;
  cursor: pointer;
  font: inherit;
}

.btn-primary:disabled,
.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .asset-details {
    flex-direction: column;
    width: 100%;
    min-width: 0;
  }
  .details-image {
    width: 100%;
    height: auto;
    flex: 0 0 auto;
    aspect-ratio: 16/9;
  }
}
</style>
